import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Clapperboard, X, Loader2, CheckCircle, AlertCircle, Play, Download, Upload, Link2, Image, Film, Scissors, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const DreamActorNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [imageLocal, setImageLocal] = useState(null);
  const [videoLocal, setVideoLocal] = useState(null);
  const [cutFirstSecond, setCutFirstSecond] = useState(data.cutFirstSecond ?? true);
  const [localError, setLocalError] = useState(null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openVideoPreview } = useMediaPreview();

  // Check for connected inputs
  const imageEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputImage');
  const videoEdge = edges.find((e) => e.target === id && e.targetHandle === 'inputVideo');
  
  // Get connected node data
  const getConnectedNodeData = (edge) => {
    if (!edge) return null;
    const sourceNode = nodes.find((n) => n.id === edge.source);
    return sourceNode?.data;
  };
  
  const imageInputData = getConnectedNodeData(imageEdge);
  const videoInputData = getConnectedNodeData(videoEdge);
  
  // Determine which files to use (connected or local upload)
  const imageSource = imageInputData?.hasOutput ? { 
    file: imageInputData.file, 
    preview: imageInputData.outputUrl || imageInputData.preview,
    name: imageInputData.fileName || 'connected_image',
    fromConnection: true
  } : imageLocal;
  
  const videoSource = videoInputData?.hasOutput ? { 
    file: videoInputData.file, 
    preview: videoInputData.outputUrl || videoInputData.preview,
    name: videoInputData.fileName || 'connected_video',
    fromConnection: true
  } : videoLocal;

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageLocal({ file, preview: e.target.result, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (file) => {
    if (!file) return;
    setVideoLocal({ file, preview: URL.createObjectURL(file), name: file.name });
  };

  const handleSourcePickImage = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleImageChange(result.file);
      } else if (result.url) {
        setImageLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['image'], 'image/jpeg,image/jpg,image/png');
  }, [id, openSourcePicker]);

  const handleSourcePickVideo = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleVideoChange(result.file);
      } else if (result.url) {
        setVideoLocal({ file: null, preview: result.url, name: result.fileName, fromGallery: true });
      }
    });
    openSourcePicker(id, ['video'], 'video/mp4,video/mov,video/webm');
  }, [id, openSourcePicker]);

  // Fetch file from URL if coming from connection
  const fetchFileFromUrl = async (url, filename, isVideo = false) =>
    blobToFile(url, filename, isVideo ? 'video/mp4' : 'image/jpeg');

  const handleProcess = useCallback(async () => {
    let imageFile = imageSource?.file;
    let videoFile = videoSource?.file;
    const directParams = { cut_first_second: cutFirstSecond };
    const files = [];

    if (!imageFile && imageSource?.preview) {
      if (imageSource.preview.startsWith('http')) {
        directParams.image_url = imageSource.preview;
      } else {
        imageFile = await fetchFileFromUrl(imageSource.preview, imageSource.name, false);
      }
    }
    if (imageFile) files.push({ key: 'image_url', file: imageFile });

    if (!videoFile && videoSource?.preview) {
      if (videoSource.preview.startsWith('http')) {
        directParams.video_url = videoSource.preview;
      } else {
        videoFile = await fetchFileFromUrl(videoSource.preview, videoSource.name, true);
      }
    }
    if (videoFile) files.push({ key: 'video_url', file: videoFile });

    if (!imageFile && !directParams.image_url) {
      setLocalError('No se pudo obtener la imagen. Verifica la conexión o sube una imagen.');
      return;
    }
    if (!videoFile && !directParams.video_url) {
      setLocalError('No se pudo obtener el video. Verifica la conexión o sube un video.');
      return;
    }
    setLocalError(null);
    resetExec();

    const result = await processNode({
      resultType: 'dream_actor',
      files,
      params: directParams,
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'dreamActor',
        filename: `dreamactor_${imageSource?.name || 'image'}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, imageSource, videoSource, cutFirstSecond, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const hasValidImage = imageSource?.preview || imageSource?.file || (imageInputData?.hasOutput);
  const hasValidVideo = videoSource?.preview || videoSource?.file || (videoInputData?.hasOutput);
  const canProcess = hasValidImage && hasValidVideo;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', hasValidImage, { connected: !!imageEdge }), inputNeed('video', hasValidVideo, { connected: !!videoEdge })]);

  return (
    <div className={`node-container dreamactor-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="dreamactor-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="dreamactor-reconfigure-notice"
        />
      )}
      <Handle
        type="target"
        position={Position.Left}
        id="inputImage"
        className="node-handle input-handle dreamactor-handle-image"
        style={{ top: '30%' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="inputVideo"
        className="node-handle input-handle dreamactor-handle-video"
        style={{ top: '55%' }}
      />
      
      {/* Header */}
      <div className="node-header dreamactor-header">
        <div className="node-header-left">
          <Clapperboard size={16} />
          <span>DreamActor</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content dreamactor-content">
        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Transfiriendo movimiento...</span>
            </div>
            <p className="node-processing-hint">
              Aplicando movimientos del video a la imagen
            </p>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <video 
              src={data.outputUrl} 
              controls 
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'DreamActor - Animación')}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="dreamactor-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="dreamactor-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openVideoPreview(data.outputUrl, 'DreamActor - Animación')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `dreamactor_${Date.now()}.mp4`); }}
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="dreamactor" testIdPrefix="dreamactor-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Animación creada</span>
            </div>
          </div>
        ) : (
          <>
            {/* Input Files Grid */}
            <div className="dreamactor-inputs-grid">
              {/* Image Input */}
              <div className="dreamactor-input-slot">
                <span className="dreamactor-input-label">
                  <Image size={10} />
                  Imagen
                  {imageEdge && <Link2 size={10} className="dreamactor-connected-icon" />}
                </span>
                {imageSource?.preview ? (
                  <div className="dreamactor-preview-box image">
                    <img src={imageSource.preview} alt="Source" />
                    {!imageSource.fromConnection && (
                      <button 
                        className="dreamactor-clear-btn"
                        onClick={() => setImageLocal(null)}
                      >
                        <X size={10} />
                      </button>
                    )}
                    {imageSource.fromConnection && (
                      <div className="dreamactor-connected-badge">
                        <Link2 size={8} />
                      </div>
                    )}
                  </div>
                ) : imageEdge ? (
                  <div className="dreamactor-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickImage} data-testid="load-btn-dream-image">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>

              {/* Video Input */}
              <div className="dreamactor-input-slot">
                <span className="dreamactor-input-label">
                  <Film size={10} />
                  Video Ref.
                  {videoEdge && <Link2 size={10} className="dreamactor-connected-icon" />}
                </span>
                {videoSource?.name ? (
                  <div className="dreamactor-preview-box video">
                    {videoSource.preview ? (
                      <video src={videoSource.preview} muted />
                    ) : (
                      <Film size={20} />
                    )}
                    <span className="dreamactor-video-name">{videoSource.name.slice(0, 10)}...</span>
                    {!videoSource.fromConnection && (
                      <button 
                        className="dreamactor-clear-btn"
                        onClick={() => setVideoLocal(null)}
                      >
                        <X size={10} />
                      </button>
                    )}
                    {videoSource.fromConnection && (
                      <div className="dreamactor-connected-badge">
                        <Link2 size={8} />
                      </div>
                    )}
                  </div>
                ) : videoEdge ? (
                  <div className="dreamactor-waiting-input">
                    <Link2 size={14} />
                    <span>Esperando...</span>
                  </div>
                ) : (
                  <button type="button" className="node-load-btn" onClick={handleSourcePickVideo} data-testid="load-btn-dream-video">
                    <Upload size={14} /> Cargar
                  </button>
                )}
              </div>
            </div>

            {/* Cut First Second Option */}
            <div className="dreamactor-settings">
              <label className="dreamactor-checkbox">
                <input
                  type="checkbox"
                  checked={cutFirstSecond}
                  onChange={(e) => setCutFirstSecond(e.target.checked)}
                />
                <span className="dreamactor-checkbox-custom">
                  <Scissors size={10} />
                </span>
                <span>Cortar primer segundo</span>
              </label>
              <p className="dreamactor-hint">Elimina la transición inicial</p>
            </div>

            {/* Info text */}
            <p className="dreamactor-info">
              Transfiere movimientos de un video a cualquier imagen (humanos, cartoons, animales)
            </p>

            {/* Process Button */}
            <button
              className="node-process-btn dreamactor-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="dreamactor-generate-btn"
            >
              <Play size={14} />
              Animar Imagen
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError || localError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

DreamActorNode.displayName = 'DreamActorNode';

export default DreamActorNode;
